import { FC, Fragment, useMemo } from "react";
import { Chip, Popover, PopoverOrigin, Stack } from "@mui/material";
import { NatGatewayIdCellRendererPopoverContent } from "./NatGatewayIdCellRendererPopoverContent";
import { readDataByDotSeparatedString } from "../../../../../ag-grid/utils/helpers/readDataByDotSeparatedString";
import type { ICellRendererParams } from "ag-grid-community";
import { FilteredNatGatewayResourceItem } from "@/services/cloudchipr.api";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";

export const NatGatewayIdCellRenderer: FC<
  ICellRendererParams<FilteredNatGatewayResourceItem>
> = ({ data, column }) => {
  const colId = column?.getColId() ?? "";

  const ids = useMemo(
    () => (readDataByDotSeparatedString(colId, data) ?? []) as string[],
    [colId, data],
  );

  const { anchor, open, openMenu, closeMenu } = useMenuHook();

  const secondaryValues = useMemo(() => {
    if (!ids) {
      return [];
    }
    return ids?.slice(1);
  }, [ids]);

  if (!ids?.length) {
    return <div>N/A</div>;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <TypographyWithTooltip title={ids.at(0)} variant="body2" />

      {secondaryValues?.length > 1 && (
        <Fragment>
          <Chip
            label={`+${secondaryValues.length}`}
            onClick={openMenu}
            variant="outlined"
            size="small"
            sx={{ ml: 0.5 }}
          />
          <Popover
            open={open}
            anchorEl={anchor}
            onClose={closeMenu}
            transformOrigin={transformOrigin}
            anchorOrigin={anchorOrigin}
            slotProps={slotProps}
          >
            <NatGatewayIdCellRendererPopoverContent
              primaryValue={ids.at(0) ?? ""}
              secondaryValues={secondaryValues}
              columnKey={colId.split(".").at(1) ?? ""}
            />
          </Popover>
        </Fragment>
      )}
    </Stack>
  );
};

const transformOrigin: PopoverOrigin = {
  horizontal: "right",
  vertical: "top",
};
const anchorOrigin: PopoverOrigin = {
  horizontal: "center",
  vertical: "bottom",
};
const slotProps = {
  paper: {
    style: { maxHeight: 400, minWidth: 220, maxWidth: 400 },
  },
};
